// =============================================================================
// Workspace Types - indxone.workspace.v1
// Canonical data model for Phase 1
// =============================================================================

// =============================================================================
// Organization types for multi-profile support
// =============================================================================

export type OrganizationType = 'freelance' | 'tpe_pme' | 'public';

// =============================================================================

/** Metadata about the workspace version and migration state */
export interface WorkspaceMeta {
  schema: number;
  version: string;
  createdAt: number;
  updatedAt: number;
  migratedFrom?: string[];
  migrationRecords?: MigrationRecord[];
  demoDismissed?: boolean;
  /** Appareil ayant produit la dernière version cloud (information d'interface uniquement). */
  deviceLabel?: string;
}

/** Record of a migration that was performed */
export interface MigrationRecord {
  source: string;
  timestamp: number;
  success: boolean;
  message?: string;
}

/** Project context - basic information about the mission/project */
export interface ProjectContext {
  id: string;
  name: string;
  client: string;
  owner: string;
  description?: string;
  scope?: string;
  budget?: number;
  startDate?: number;
  endDate?: number;
  tjm?: number;
  daysPerMonth?: number;
  currency?: string;
  archivedAt?: number;
  createdAt: number;
  updatedAt: number;
  canvasId?: string;
  sipocId?: string;
  moscowId?: string;
  estimationId?: string;
  proposalId?: string;
  quoteId?: string;
  dealRoomId?: string;
  memoryId?: string;
  missionScore?: number;
  missionStatus?: 'draft' | 'cadrage' | 'active' | 'closed';
  metadata?: Record<string, string>;
}

export interface MissionCanvasSection {
  content: string;
  completionPct: number;
}

export interface MissionCanvas {
  id: string;
  projectId: string;
  why: MissionCanvasSection;
  objective: MissionCanvasSection;
  stakeholders: MissionCanvasSection;
  constraints: {
    budget: string;
    deadline: string;
    other: string;
  };
  risks: MissionCanvasSection;
  successCriteria: MissionCanvasSection;
  score: number;
  scoreVerdict: 'flou' | 'cadrable' | 'solide' | 'pret';
  createdAt: number;
  updatedAt: number;
}

/** Evaluation model for mission assessment */
export interface Evaluation {
  id: string;
  projectId: string;
  scores: {
    strategicAlignment: number;
    technicalComplexity: number;
    teamReadiness: number;
    businessValue: number;
    riskLevel: number;
  };
  stress?: {
    mentalLoad: number;
    clientComplexity: number;
    rhythmIntensity: number;
    clientDependency: number;
  };
  verdict: 'GO' | 'NO_GO' | 'CONDITIONAL';
  totalScore?: number;
  comments?: string;
  recommendations?: string[];
  calculatedAt: number;
  meta: {
    version: number;
    calculatedBy: string;
  };
}

/** SIPOC model - Suppliers, Inputs, Process, Outputs, Customers */
export interface Sipoc {
  id: string;
  projectId: string;
  name: string;
  owner?: string;
  suppliers: SipocColumn;
  inputs: SipocColumn;
  process: SipocColumn;
  outputs: SipocColumn;
  customers: SipocColumn;
  projectScope?: string;
  notes?: string[];
  scope: 'SIMPLE' | 'TECHNICAL' | 'BUSINESS';
  createdAt: number;
  updatedAt: number;
}

export interface SipocColumn {
  items: SipocItem[];
}

export interface SipocItem {
  id: string;
  title: string;
  description?: string;
  order: number;
}

/** MoSCoW model - Must have, Should have, Could have, Won't have */
export interface Moscow {
  id: string;
  projectId: string;
  name: string;
  budgetDays?: number;
  teamSize?: number;
  categories: {
    must: MoscowCategory;
    should: MoscowCategory;
    could: MoscowCategory;
    out?: MoscowCategory;
    wont: MoscowCategory;
  };
  prioritizationMethod?: 'value-effort' | 'impact-urgency' | 'custom';
  createdAt: number;
  updatedAt: number;
}

export interface MoscowCategory {
  items: MoscowItem[];
}

export interface MoscowItem {
  id: string;
  title: string;
  description?: string;
  value?: number;
  effort?: number;
  priorityScore?: number;
  order: number;
}

/** RACI model - Responsible, Accountable, Consulted, Informed */
export interface Raci {
  id: string;
  projectId: string;
  name: string;
  roles: RaciRole[];
  tasks: RaciTask[];
  matrix: RaciMatrixEntry[];
  sponsor?: string;
  createdAt: number;
  updatedAt: number;
}

export interface RaciRole {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

export interface RaciTask {
  id: string;
  title: string;
  description?: string;
  mustHave?: boolean;
  shouldHave?: boolean;
  order?: number;
}

export interface RaciMatrixEntry {
  roleId: string;
  taskId: string;
  assignment: 'R' | 'A' | 'C' | 'I';
}

export interface DealRoomMilestone {
  id: string;
  label?: string;
  date?: string;
  done: boolean;
  title?: string;
}

export interface DealRoom {
  id: string;
  projectId: string;
  name: string;
  client: string;
  owner?: string;
  status: 'prospect' | 'en-cours' | 'cadre';
  objective?: string;
  context?: string;
  scope?: string;
  deliverables: string[];
  tasks: DealRoomTask[];
  milestones: DealRoomMilestone[];
  risks: string[];
  decisions: string[];
  timeline?: {
    startDate?: string;
    endDate?: string;
  };
  progressPct?: number;
  nextSteps?: string;
  createdAt: number;
  updatedAt: number;
}

export interface DealRoomTask {
  id: string;
  title: string;
  status: 'todo' | 'doing' | 'done';
}

export type CraDayState = 'travaille' | 'demi' | 'conge';

export interface CraMonth {
  id: string;
  projectId: string;
  year: number;
  month: number;
  consultant: string;
  client: string;
  mission: string;
  pricingMode?: 'tjm' | 'forfait';
  tjm: number;
  tarifForfait?: number;
  dayStates: Record<string, CraDayState>;
  workedDays: number;
  leaveDays: number;
  revenue: number;
  invoiceStatus?: 'none' | 'pending' | 'invoiced' | 'paid' | 'overdue';
  invoicedAt?: string;
  createdAt: number;
  updatedAt: number;
}

export interface TreasuryCharge {
  id: string;
  label: string;
  amount: number;
  kind?: 'fixe' | 'variable';
}

export type TaxStrategy = 'dividendes' | 'remuneration' | 'mixte';

export type HouseholdSituation = 'celibataire' | 'couple' | 'parent-isole' | 'veuf';

export const HOUSEHOLD_SITUATION_LABELS: Record<HouseholdSituation, string> = {
  'celibataire': 'Célibataire / Divorcé(e)',
  'couple': 'Marié(e) / Pacsé(e)',
  'parent-isole': 'Parent isolé (case T)',
  'veuf': 'Veuf / Veuve',
};

export function computeHouseholdParts(situation: HouseholdSituation, children: number): number {
  const base = situation === 'celibataire' || situation === 'parent-isole' ? 1 : 2;
  let childrenParts = 0;
  for (let i = 0; i < children; i++) {
    childrenParts += i < 2 ? 0.5 : 1;
  }
  const parentIsoleBonus = situation === 'parent-isole' && children >= 1 ? 0.5 : 0;
  return base + childrenParts + parentIsoleBonus;
}

export type EiCategory = 'bic-vente' | 'bic-services' | 'bnc-liberal' | 'bnc-cipav';

export const EI_CATEGORY_LABELS: Record<EiCategory, string> = {
  'bic-vente': 'BIC vente (12,3%)',
  'bic-services': 'BIC services (21,2%)',
  'bnc-liberal': 'BNC libéral (25,6%)',
  'bnc-cipav': 'BNC CIPAV (23,2%)',
};

export const EI_CATEGORY_RATES: Record<EiCategory, { urssaf: number; cfp: number }> = {
  'bic-vente': { urssaf: 0.123, cfp: 0.002 },
  'bic-services': { urssaf: 0.212, cfp: 0.003 },
  'bnc-liberal': { urssaf: 0.256, cfp: 0.002 },
  'bnc-cipav': { urssaf: 0.232, cfp: 0.002 },
};

export interface TreasuryProfile {
  id: string;
  projectId: string;
  status: 'sasu' | 'ei' | 'eurl' | 'portage';
  pricingMode?: 'tjm' | 'prestation';
  tarifPrestation?: number;
  joursPrestation?: number;
  openingBalance: number;
  caYtd: number;
  horizonMonths: number;
  daysPerMonth: number;
  tjm: number;
  charges: TreasuryCharge[];
  taxStrategy?: TaxStrategy;
  salaryNetTarget?: number;
  dividendPayoutPct?: number;
  vatRegistered?: boolean;
  eiCategory?: EiCategory;
  acre?: boolean;
  versementLiberatoire?: boolean;
  householdSituation?: HouseholdSituation;
  children?: number;
  createdAt: number;
  updatedAt: number;
}

export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  role: string;
  mode: 'guided' | 'expert';
  phone?: string;
  website?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  siret?: string;
  vatNumber?: string;
  iban?: string;
  rib?: string;
  bankName?: string;
  logoDataUrl?: string;
  headline?: string;
  notes?: string;
  status: TreasuryProfile['status'];
  netTarget: number;
  tjm: number;
  daysPerMonth: number;
  eiCategory?: EiCategory;
  acre?: boolean;
  versementLiberatoire?: boolean;
  householdSituation?: HouseholdSituation;
  children?: number;
  taxStrategy?: TaxStrategy;
  vatRegistered?: boolean;
  sector?: string;
  teamSize?: string;
  revenueTarget?: number;
  organizationType?: OrganizationType;
  createdAt: number;
  updatedAt: number;
}

export interface MissionCostPhase {
  label: string;
  startMonth: number;
  endMonth: number;
  daysPerWeek: number;
  fixedCosts: number;
  variableCosts: number;
  extraCosts: number;
}

export interface CalcMissionSimpleDraft {
  name: string;
  client: string;
  pricingMode?: 'tjm' | 'prestation';
  tjm: number;
  days: number;
  fixedCosts: number;
  variableCosts: number;
  extraCosts: number;
  netTarget: number;
  successRate: number;
  contingencyPct: number;
  status: TreasuryProfile['status'];
  missionDuration?: number;
  totalBudget?: number;
  tarifPrestation?: number;
  joursPrestation?: number;
  phases?: MissionCostPhase[];
}

// =============================================================================
// Estimation
// =============================================================================

export type EstimationSize = 'XS' | 'S' | 'M' | 'L' | 'XL';

export const ESTIMATION_SIZES: EstimationSize[] = ['XS', 'S', 'M', 'L', 'XL'];

export const DEFAULT_SIZE_MAP: Record<EstimationSize, number> = {
  XS: 0.5,
  S: 1,
  M: 3,
  L: 8,
  XL: 15,
};

export interface EstimationFeature {
  id: string;
  label: string;
  size: EstimationSize;
  note?: string;
}

export interface EstimationPhase {
  id: string;
  label: string;
  features: EstimationFeature[];
}

export interface Estimation {
  id: string;
  name: string;
  client?: string;
  pricingMode?: 'tjm' | 'forfait';
  tjm: number;
  tarifForfait?: number;
  sizeMap: Record<EstimationSize, number>;
  phases: EstimationPhase[];
  bufferCadragePct: number;
  bufferRisquePct: number;
  linkedDevisId?: string;
  linkedProjectId?: string;
  createdAt: number;
  updatedAt: number;
}

export type CalcMissionSessionMode = 'simple' | 'scratchpad';

export interface CalcMissionSession {
  id: string;
  projectId?: string;
  name: string;
  mode: CalcMissionSessionMode;
  templateKey?: string;
  simple: CalcMissionSimpleDraft;
  scratchpad: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export type MissionLeadStatus = 'prospect' | 'interview' | 'negotiation' | 'signed' | 'active' | 'done';

export const MISSION_LEAD_STATUSES: MissionLeadStatus[] = ['prospect', 'interview', 'negotiation', 'signed', 'active', 'done'];
export const MISSION_LEAD_STATUS_LABELS: Record<MissionLeadStatus, string> = {
  prospect: 'Prospect',
  interview: 'Entretien',
  negotiation: 'Négociation',
  signed: 'Signé',
  active: 'En cours',
  done: 'Terminé',
};

export interface MissionLead {
  id: string;
  clientName: string;
  tjm: number;
  startDate?: string;
  notes?: string;
  status: MissionLeadStatus;
  order: number;
  createdAt: number;
  updatedAt: number;
}

export type NoteTag = 'contexte' | 'vigilance' | 'prochaine-etape' | 'autre';
export const NOTE_TAGS: NoteTag[] = ['contexte', 'vigilance', 'prochaine-etape', 'autre'];
export const NOTE_TAG_LABELS: Record<NoteTag, string> = {
  'contexte': 'Contexte',
  'vigilance': 'Vigilance',
  'prochaine-etape': 'Prochaine étape',
  'autre': 'Autre',
};

export interface WorkspaceNote {
  id: string;
  content: string;
  tag?: NoteTag;
  projectId?: string;
  createdAt: number;
  updatedAt: number;
}

export interface WorkspaceTimelineEntry {
  id: string;
  kind: 'decision' | 'hypothese' | 'version' | 'negociation' | 'arbitrage' | 'export' | 'changement';
  label: string;
  detail?: string;
  delta?: Record<string, { before?: number; after: number }>;
  createdAt: number;
}

/** Main Workspace structure - single source of truth */
export interface Workspace {
  meta: WorkspaceMeta;
  currentProjectId?: string;
  currentCalcMissionSessionId?: string;
  activeMissionId?: string | null;
  projects: ProjectContext[];
  evaluations: Evaluation[];
  sipocs: Sipoc[];
  moscows: Moscow[];
  racis: Raci[];
  dealRooms: DealRoom[];
  cras: CraMonth[];
  treasuries: TreasuryProfile[];
  canvases: MissionCanvas[];
  profile: UserProfile;
  calcMissionSessions: CalcMissionSession[];
  estimations: Estimation[];
  variables: Record<string, number>;
  notes: WorkspaceNote[];
  timeline: WorkspaceTimelineEntry[];
  preferences: {
    theme: 'dark' | 'light';
    language: string;
  };
}

export interface WorkspaceExportEnvelope {
  kind: 'indxone-workspace-export';
  snapshotVersion: '1.0.0';
  exportedAt: number;
  appVersion: string;
  schema: number;
  workspace: Workspace;
}

/** Legacy compatibility types */
export interface LegacyHubState {
  schema: number;
  project: {
    name: string;
    client: string;
    owner: string;
  };
  eval?: { data: unknown; meta: { ts: number; v: number } };
  sipoc?: { data: unknown; meta: { ts: number; v: number } };
  raci?: { data: unknown; meta: { ts: number; v: number } };
  moscow?: { data: unknown; meta: { ts: number; v: number } };
  calc?: { data: unknown; meta: { ts: number; v: number } };
  progress: Record<string, boolean>;
}

export interface MissionMemory {
  id: string;
  clientName: string;
  tjm: number;
  duration: string;
  stack: string;
  rating: number;
  feedback: string;
  avoid: boolean;
  sector?: string;   // texte libre : "Marketing", "Finance", "Santé"...
  budget?: number;   // budget total en €
  leadId?: string;
  createdAt: number;
  updatedAt: number;
}

export function createMissionMemoryDraft(overrides?: Partial<MissionMemory>): MissionMemory {
  const now = Date.now();
  return {
    id: `mem_${now}_${Math.random().toString(36).slice(2, 7)}`,
    clientName: '',
    tjm: 0,
    duration: '',
    stack: '',
    rating: 3,
    feedback: '',
    avoid: false,
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
}

export type WorkspaceSnapshot = Workspace;
export type WorkspaceImportPayload = Workspace | WorkspaceExportEnvelope;

export type ClientRiskLevel = 'low' | 'medium' | 'high';

export interface ClientRiskAnswers {
  clientType: 'startup' | 'pme' | 'grand_compte' | 'public' | 'particulier';
  contractType: 'regie' | 'forfait' | 'mixte';
  missionDuration: 'court' | 'moyen' | 'long';
  budgetValidated: 'yes' | 'partial' | 'no' | 'unknown';
  paymentHistory: 'good' | 'unknown' | 'issues';
  references: 'yes' | 'no' | 'unknown';
  scopeClarity?: 'clear' | 'vague' | 'none';
  decisionMaker?: 'yes' | 'no' | 'unclear';
  contractualProtection?: 'yes' | 'no';
}

export interface RiskFactor {
  label: string;
  weight: number;
  level: ClientRiskLevel;
}

export interface ClientRiskRecord {
  id: string;
  name: string;
  evaluatedAt: number;
  score: number;
  level: ClientRiskLevel;
  answers: ClientRiskAnswers;
  factors: RiskFactor[];
  note?: string;
  createdAt: number;
  updatedAt: number;
}
